/**
 * ImportOrdemContagemModal - Modal for Ordem de Contagem Excel import (Epic 53)
 *
 * Imports Excel file with 8 columns to create new ItemContagemImportado records
 * Integrates with POST /api/contagens/{job_id}/importar-ordem
 *
 * Per-client replace/append:
 * - First import → direct (no confirmation)
 * - New clients → append (existing items untouched)
 * - Existing clients → confirmation dialog before replacing
 */
import React, { useState } from 'react';
import { X, Upload, FileText, AlertCircle, Info, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Users, ClipboardPaste } from 'lucide-react';
import contagemService, { ImportOrdemResponse, ConfirmReplaceOrdemResponse } from '../../services/contagemService';
import { tsvToXlsxFile, parseTsv } from '../../utils/pasteToXlsx';

const MAX_ERRORS_SHOWN = 20;

interface ImportOrdemContagemModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: number;
  onImportSuccess: (data: ImportOrdemResponse) => void;
}

// Column definitions for the tooltip
const ORDEM_COLUMNS = [
  { col: 'A', name: 'Código Cliente', required: true, description: 'CNPJ do cliente' },
  { col: 'B', name: 'Regime Especial', required: true, description: 'SIM ou NAO' },
  { col: 'C', name: 'Código', required: true, description: 'Código do produto' },
  { col: 'D', name: 'Descrição', required: false, description: 'Descrição do produto' },
  { col: 'E', name: 'Lote', required: true, description: 'Número do lote' },
  { col: 'F', name: 'Validade', required: true, description: 'DD/MM/YYYY' },
  { col: 'G', name: 'Qtde (Sistema)', required: true, description: 'Quantidade no sistema' },
  { col: 'H', name: 'Coletar', required: false, description: 'Quantidade a coletar' },
];

function ImportOrdemResult({ result, onClose }: { result: ImportOrdemResponse; onClose: () => void }) {
  const hasErrors = (result.total_erros > 0) || (result.total_nao_encontrados && result.total_nao_encontrados > 0);
  const hasSuccess = result.total_criados > 0;
  const allErrors = [
    ...(result.erros || []),
  ];

  const bgClass = !hasSuccess
    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    : hasErrors
    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';

  return (
    <div className="space-y-4">
      <div className={`rounded-lg p-4 border ${bgClass}`}>
        {/* Success line */}
        {result.total_criados > 0 && (
          <p className="text-sm text-green-700 dark:text-green-300 mb-1">
            ✅ {result.total_criados} itens importados com sucesso
          </p>
        )}

        {/* Vinculados */}
        {result.total_vinculados != null && result.total_vinculados > 0 && (
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">
            🔗 {result.total_vinculados} vinculados ao catálogo
          </p>
        )}

        {/* Não encontrados */}
        {result.total_nao_encontrados != null && result.total_nao_encontrados > 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400 mb-1">
            ⚠️ {result.total_nao_encontrados} produtos não encontrados no catálogo
          </p>
        )}

        {/* Consolidados */}
        {result.total_consolidados != null && result.total_consolidados > 0 && (
          <p className="text-sm text-purple-700 dark:text-purple-300 mb-1">
            🔄 {result.total_consolidados} linhas duplicadas consolidadas
          </p>
        )}

        {/* Error details — Story 2.13: render defensivo aceita string OR objeto.
            Backend legado podia retornar erros como strings tipo "Linha X: msg",
            resultando em "Linha : []" vazio. Agora extrai linha + msg de ambos
            formatos. */}
        {allErrors.length > 0 && (
          <div className="mt-2">
            <p className="text-sm text-red-700 dark:text-red-300 mb-1">
              ❌ {result.total_erros} erro(s) encontrado(s):
            </p>
            <ul className="text-xs text-red-600 dark:text-red-400 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
              {allErrors.slice(0, MAX_ERRORS_SHOWN).map((e: unknown, i) => {
                const isString = typeof e === 'string';
                let linha: string | number = '?';
                let codigo = '';
                let mensagem = '';
                if (isString) {
                  // formato legacy "Linha 5: Código obrigatório"
                  const m = (e as string).match(/^Linha\s+(\d+):\s*(.+)$/);
                  if (m) {
                    linha = parseInt(m[1], 10);
                    mensagem = m[2].trim();
                  } else {
                    mensagem = e as string;
                  }
                } else {
                  const obj = e as { linha?: number | string; codigo?: string; erro?: string };
                  linha = obj.linha ?? '?';
                  codigo = obj.codigo ?? '';
                  mensagem = obj.erro ?? 'Erro não especificado';
                }
                return (
                  <li key={i}>
                    <span className="font-semibold">Linha {linha}:</span>{' '}
                    {codigo && <code className="bg-red-50 dark:bg-red-900/40 px-1 rounded">{codigo}</code>}{' '}
                    {mensagem}
                  </li>
                );
              })}
              {allErrors.length > MAX_ERRORS_SHOWN && (
                <li className="text-gray-500 dark:text-gray-400">
                  ... e mais {allErrors.length - MAX_ERRORS_SHOWN} erro(s)
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Story 2.30/2.61: Avisos nao-bloqueantes da importacao.
            Cobre: produtos sem GTIN (importados, contar manual) E produtos
            excluidos do cadastro (Story 2.61: NAO importados). Cada item traz
            o texto especifico em w.aviso, entao o titulo aqui e generico. */}
        {result.warnings && result.warnings.length > 0 && (
          <div className="mt-3 p-3 rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300 mb-1.5">
              ⚠️ {result.warnings.length} {result.warnings.length === 1 ? 'aviso na importação' : 'avisos na importação'}:
            </p>
            <ul className="text-xs text-yellow-700 dark:text-yellow-400 list-disc list-inside max-h-40 overflow-y-auto space-y-0.5">
              {result.warnings.slice(0, MAX_ERRORS_SHOWN).map((w, i) => (
                <li key={i}>
                  <span className="font-semibold">Linha {w.linha}:</span>{' '}
                  <code className="bg-yellow-100 dark:bg-yellow-900/40 px-1 rounded">{w.codigo}</code>{' '}
                  {w.aviso}
                </li>
              ))}
              {result.warnings.length > MAX_ERRORS_SHOWN && (
                <li className="text-gray-500 dark:text-gray-400">
                  ... e mais {result.warnings.length - MAX_ERRORS_SHOWN} aviso(s)
                </li>
              )}
            </ul>
          </div>
        )}

        {/* All success, no errors */}
        {!hasErrors && hasSuccess && (
          <p className="text-sm text-green-700 dark:text-green-300 mt-1">
            Todas as {result.total_linhas} linhas processadas sem erros.
          </p>
        )}
      </div>

      {/* Close button */}
      <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={onClose}
          className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}

export default function ImportOrdemContagemModal({
  isOpen,
  onClose,
  jobId,
  onImportSuccess,
}: ImportOrdemContagemModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  const [confirmData, setConfirmData] = useState<ConfirmReplaceOrdemResponse | null>(null);
  const [importResult, setImportResult] = useState<ImportOrdemResponse | null>(null);
  // #15: modo arquivo OU colar do Excel (TSV)
  const [mode, setMode] = useState<'arquivo' | 'colar'>('arquivo');
  const [pastedText, setPastedText] = useState('');
  const pastedRows = parseTsv(pastedText).length;

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        setError('Arquivo deve ser Excel (.xlsx ou .xls)');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
      setConfirmData(null);
    }
  };

  const handleSuccess = (response: ImportOrdemResponse) => {
    console.log('Import Ordem success:', response);
    setImportResult(response);
    setConfirmData(null);
    onImportSuccess(response);
  };

  const handleUpload = async () => {
    // #15: resolve o arquivo (arquivo selecionado OU texto colado -> xlsx).
    // Guarda em selectedFile para o fluxo de confirmacao (handleConfirmReplace) reusar.
    let fileToSend: File | null = selectedFile;
    if (mode === 'colar') {
      fileToSend = tsvToXlsxFile(pastedText, `base_${jobId}.xlsx`);
      if (!fileToSend) {
        setError('Cole os dados do Excel primeiro');
        return;
      }
      setSelectedFile(fileToSend);
    } else if (!selectedFile) {
      setError('Selecione um arquivo primeiro');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // First call: analyze (force_replace=false)
      const response = await contagemService.importarOrdemContagem(jobId, fileToSend as File);

      if ('confirm_replace' in response && response.confirm_replace) {
        // Show confirmation dialog
        setConfirmData(response);
        return;
      }

      // No conflicts — direct import succeeded
      handleSuccess(response as ImportOrdemResponse);
    } catch (err: any) {
      console.error('Import Ordem error:', err);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Erro ao fazer upload do arquivo'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmReplace = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setError(null);

      // Second call: execute with force_replace=true
      const response = await contagemService.importarOrdemContagem(jobId, selectedFile, true);
      handleSuccess(response as ImportOrdemResponse);
    } catch (err: any) {
      console.error('Import Ordem confirm error:', err);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Erro ao confirmar importação'
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPastedText('');
    setMode('arquivo');
    setError(null);
    setConfirmData(null);
    setImportResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full">
        {/* Header - Green/Emerald color */}
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 px-6 py-4 flex items-center justify-between rounded-t-xl">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Upload size={24} />
            Importar Base da Contagem
          </h2>
          <button
            onClick={handleClose}
            disabled={uploading}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Import Result */}
          {importResult ? (
            <ImportOrdemResult result={importResult} onClose={handleClose} />
          ) : confirmData ? (
            <div className="space-y-4">
              {/* Warning Header */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded flex items-start gap-3">
                <AlertTriangle size={22} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    Confirmação Necessária
                  </h4>
                  <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                    O arquivo contém itens para clientes que já possuem dados importados.
                    Os itens desses clientes serão <strong>substituídos</strong>.
                  </p>
                </div>
              </div>

              {/* Clients to REPLACE */}
              {confirmData.clients_to_replace.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2 flex items-center gap-2">
                    <Users size={16} />
                    Clientes a Substituir ({confirmData.clients_to_replace.length})
                  </h4>
                  <div className="space-y-2">
                    {confirmData.clients_to_replace.map((c) => (
                      <div
                        key={c.cliente_id}
                        className="flex items-center justify-between text-sm bg-white dark:bg-gray-700 rounded px-3 py-2"
                      >
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {c.cliente_nome}
                        </span>
                        <span className="text-amber-700 dark:text-amber-400">
                          {c.existing_count} itens existentes → {c.new_count} novos
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Clients to APPEND */}
              {confirmData.clients_to_append.length > 0 && (
                <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Novos Clientes ({confirmData.clients_to_append.length})
                  </h4>
                  <div className="space-y-2">
                    {confirmData.clients_to_append.map((c) => (
                      <div
                        key={c.cliente_id}
                        className="flex items-center justify-between text-sm bg-white dark:bg-gray-700 rounded px-3 py-2"
                      >
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {c.cliente_nome}
                        </span>
                        <span className="text-green-700 dark:text-green-400">
                          + {c.new_count} novos itens
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info: other clients unaffected */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2">
                <Info size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  Itens de outros clientes <strong>NÃO</strong> serão afetados.
                </span>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 p-4 rounded">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      Processando importação...
                    </span>
                  </div>
                </div>
              )}

              {/* Confirm Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => { setConfirmData(null); setError(null); }}
                  disabled={uploading}
                  className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmReplace}
                  disabled={uploading}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-600 hover:to-orange-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload size={18} />
                  {uploading ? 'Importando...' : 'Confirmar Importação'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* #15: alternar Arquivo / Colar do Excel */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setMode('arquivo')}
                  disabled={uploading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'arquivo' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                >
                  <Upload size={16} /> Arquivo Excel
                </button>
                <button
                  type="button"
                  onClick={() => setMode('colar')}
                  disabled={uploading}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${mode === 'colar' ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
                >
                  <ClipboardPaste size={16} /> Colar do Excel
                </button>
              </div>

              {mode === 'arquivo' ? (
              /* File Input */
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Selecione o arquivo Excel:
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    className="flex-1 text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 dark:file:bg-emerald-900/20 file:text-emerald-700 dark:file:text-emerald-400 hover:file:bg-emerald-100 dark:hover:file:bg-emerald-900/30 disabled:opacity-50 cursor-pointer"
                  />
                  {selectedFile && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <FileText size={16} />
                      <span className="font-medium">{selectedFile.name}</span>
                      <span className="text-xs text-gray-500">
                        ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  )}
                </div>
              </div>
              ) : (
              /* #15: Colar do Excel (TSV) */
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Selecione as colunas no Excel, copie (Ctrl+C) e cole abaixo (Ctrl+V):
                </label>
                <textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  disabled={uploading}
                  rows={10}
                  placeholder="Cole aqui as células copiadas do Excel..."
                  className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
                />
                {pastedRows > 0 && (
                  <p className="text-xs text-gray-500 mt-1">{pastedRows} linha(s) detectada(s) (a 1ª, cabeçalho, é ignorada).</p>
                )}
              </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-4 rounded flex items-start gap-3">
                  <AlertCircle size={20} className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-red-800 dark:text-red-400">
                      Erro na Importação
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border-l-4 border-emerald-500 p-4 rounded">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></div>
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                      Enviando arquivo e processando dados...
                    </span>
                  </div>
                </div>
              )}

              {/* Collapsible Info Panel */}
              {showInfo && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-4 rounded-lg animate-in slide-in-from-top-2 duration-200">
                  <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                    Formato Esperado - 8 Colunas
                  </h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                    Este arquivo importa os <strong>dados iniciais antes da contagem física</strong>.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {ORDEM_COLUMNS.map((col) => (
                      <div
                        key={col.col}
                        className="flex items-center text-sm text-gray-700 dark:text-gray-300"
                      >
                        <span className="font-mono bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs mr-2">
                          {col.col}
                        </span>
                        <span className={col.required ? 'font-medium' : ''}>
                          {col.name}
                          {col.required && <span className="text-red-500 ml-0.5">*</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-3 italic">
                    * Campos obrigatórios. A primeira linha (cabeçalho) será ignorada.
                  </p>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleClose}
                  disabled={uploading}
                  className="px-6 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className={`flex items-center gap-2 px-4 py-2.5 border-2 rounded-lg transition font-medium ${
                    showInfo
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20'
                  }`}
                  title="Ver formato esperado"
                >
                  <Info size={18} />
                  {showInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || (mode === 'arquivo' ? !selectedFile : pastedRows === 0)}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-lg hover:from-emerald-600 hover:to-green-600 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload size={18} />
                  {uploading ? 'Enviando...' : 'Importar'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
